import React, { ChangeEvent, useEffect, useState } from "react";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import logoW from "assets/images/cmr-logo-b.png";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import { openDialog } from "state/slices/DialogSlice";
import { useTypedDispatch, useTypedSelector } from "hooks/store";
import { TextField } from "@mui/material";
import { styled } from "@mui/material/styles";
import { builder } from "Classes/global";
import { GraphicService } from "Classes/GraphicService";
import { isMobile, isIOS, browserName } from "react-device-detect";
import "./DialogSaveModel.scss";

import NoteAddIcon from "@mui/icons-material/NoteAdd";

import html2canvas from "html2canvas";

const SaveRoomText = styled(TextField)({
    "label + &": {
        color: "#155E86",
    },
    "& label.Mui-focused": {
        color: "#155E86",
    },
    ".MuiInputBase-input": {
        color: "#155E86",
        background: "#fff",
        border: "1px solid #ddd",
    },
    ".MuiFilledInput-roo:before": {
        border: "none",
    },
    "& .MuiInput-underline:after": {
        borderBottomColor: "#155E86",
    },
    "& .MuiFilledInput-root": {
        "&:after": {
            borderBottomColor: "#155E86",
        },
        "& fieldset": {
            borderColor: "#155E86",
        },
        "&:hover fieldset": {
            borderColor: "#155E86",
        },
        "&.Mui-focused fieldset": {
            borderColor: "#155E86",
        },
    },
});

const DialogSaveModelContent = (): JSX.Element => {
    const dispatch = useTypedDispatch();
    const type = useTypedSelector((store) => store.Dialog.type);
    const [sceneName, setSceneName] = useState<string>("");
    const [textError, setTextError] = useState<boolean>(false);
    const [image, setImage] = useState<string>();
    const [noteInitial, setNoteInitial] = useState<boolean>(false);

    useEffect(() => {
        // Updating default name.
        setSceneName(builder.room.currentConfiguration.name);

        const imageData = GraphicService.renderer.domElement.toDataURL();
        setImage(imageData);
    }, []);

    const handleDialogClose = (): void => {
        dispatch(openDialog({ isDialogOpen: false, type }));
    };

    const handleCaptureScene = (): void => {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const imageCont = document.getElementById("dialogImageContainer")!;

        html2canvas(imageCont, { removeContainer: true }).then(function (canvas) {
            // Check scene name is exists.
            if (sceneName.length <= 0) {
                setTextError(true);
                return;
            }

            if (isMobile && isIOS && browserName !== "Safari") {
                canvas.toBlob((blob) => {
                    if (!blob) return;

                    const imageData = URL.createObjectURL(blob);
                    window.open(imageData, "_blank");
                });
            } else {
                const imageData = canvas.toDataURL("image/jpeg");
                const newData = imageData.replace(
                    /^data:image\/png/,
                    "data:application/octet-stream"
                );

                const downloadLink = document.createElement("a");

                downloadLink.download = `${sceneName}.jpg`;
                downloadLink.href = newData;

                document.body.appendChild(downloadLink);

                downloadLink.click();

                document.body.removeChild(downloadLink);
            }
        });

        dispatch(openDialog({ isDialogOpen: false, type }));
    };

    const handleUpdateSceneName = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value as string;
        setSceneName(value);

        // Check is sceneName is empty, if yes then make validation error.
        if (value.length <= 0) {
            setTextError(true);
        } else {
            setTextError(false);
        }
    };

    useEffect(() => {
        const mathRand = (min: number, max: number) => {
            return Math.floor(Math.random() * (max - min + 1) + min);
        };

        const imageCont = document.querySelector(".dialogImageContainer") as HTMLElement;
        const saveRoom = document.getElementById("saveRoom") as HTMLElement;

        const { top, left, right, bottom } = imageCont.getBoundingClientRect();
        const yOffset = 50;
        const xOffset = 100;

        const randPosTop = mathRand(yOffset, bottom - top - yOffset);
        const randPosLeft = mathRand(xOffset, right - left - xOffset);

        const generateNote = () => {
            const noteItem = document.createElement("div");
            noteItem.className = "sticky-note-cont";

            const noteX = document.createElement("span");
            noteX.className = "sticky-note-x";
            noteX.setAttribute("data-html2canvas-ignore", "");

            const noteText = document.createElement("textarea");
            noteText.className = "sticky-note-text";

            noteItem.appendChild(noteX);
            noteItem.appendChild(noteText);
            imageCont?.appendChild(noteItem);

            const left = randPosLeft;
            const top = randPosTop;

            noteItem.style.left = left + "px";
            noteItem.style.top = top + "px";
            noteItem.style.position = "absolute";

            noteX.addEventListener("click", () => {
                noteItem.remove();
            });

            const onTouchMove = (e: TouchEvent) => {
                const rect = imageCont.getBoundingClientRect();

                const left = e.touches[0].clientX - rect.left;
                const top = e.touches[0].clientY - rect.top;

                noteItem.style.left = left + "px";
                noteItem.style.top = top + "px";
            };

            noteItem.addEventListener("touchstart", () => {
                imageCont?.addEventListener("touchmove", onTouchMove);
                saveRoom.style.overflow = "hidden";
            });

            noteItem.addEventListener("touchend", () => {
                imageCont?.removeEventListener("touchmove", onTouchMove);
                saveRoom.style.overflow = "visible";
            });

            const onMouseMove = (e: MouseEvent) => {
                const rect = imageCont.getBoundingClientRect();

                const left = e.clientX - rect.left;
                const top = e.clientY - rect.top;

                noteItem.style.left = left + "px";
                noteItem.style.top = top + "px";
            };

            noteItem.addEventListener("mousedown", () => {
                imageCont?.addEventListener("mousemove", onMouseMove);
            });

            noteItem.addEventListener("mouseup", () => {
                imageCont?.removeEventListener("mousemove", onMouseMove);
            });

            noteItem.ondragstart = function () {
                return false;
            };

            setNoteInitial(false);
        };

        if (noteInitial == true) {
            generateNote();
        }
    }, [noteInitial]);

    const noteStart = () => {
        setNoteInitial(true);
    };

    return (
        <>
            <DialogContent>
                <div className="dialogImageContainer" id="dialogImageContainer">
                    <img className="image" src={image} />
                    <div className="watermark">
                        <img src={logoW} />
                    </div>
                </div>
            </DialogContent>
            <Button
                startIcon={<NoteAddIcon />}
                fullWidth
                onClick={noteStart}
                className="btn pinkRoundedButton"
                style={{ width: "max-content", marginTop: "25px", marginBottom: "25px" }}
            >
                Add Sticky note
            </Button>
            <DialogActions className="cmrDialogActionContainer">
                <section className="saveRoomName">
                    <SaveRoomText
                        error={textError}
                        value={sceneName}
                        autoFocus={false}
                        label="Name"
                        onChange={handleUpdateSceneName}
                        helperText={textError ? "Scene name is required." : ""}
                        type="text"
                        fullWidth
                        variant="filled"
                    />
                </section>
                <section className="action">
                    <Button
                        startIcon={<CloseOutlinedIcon />}
                        fullWidth
                        onClick={handleDialogClose}
                        className="btn grayRoundedButton"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={() => handleCaptureScene()}
                        fullWidth
                        startIcon={<CheckOutlinedIcon />}
                        className="btn pinkRoundedButton"
                    >
                        Save
                    </Button>
                    <a id="downloadScene">Download</a>
                </section>
                <div id="previewScene" />
            </DialogActions>
        </>
    );
};

export default DialogSaveModelContent;
